import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import type { Send } from '../../types/send';
import { ALL_STYLES, getSendCategory, CHART_TOOLTIP_STYLE } from '../../utils/stats-utils';

const SEND_COLORS = {
  on_sight: '#22c55e',
  flash: '#f97316',
  rotpunk: '#3b82f6',
};

interface ClimbStyleDistributionChartProps {
  sends: Send[];
}

export default function ClimbStyleDistributionChart({ sends }: ClimbStyleDistributionChartProps) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    return ALL_STYLES.map(style => {
      const styleSends = sends.filter(s => s.attributes.climb_style?.includes(style));
      return {
        label: t(`climbTypes.${style}`),
        on_sight: styleSends.filter(s => getSendCategory(s.attributes.send_type) === 'on_sight').length,
        flash: styleSends.filter(s => getSendCategory(s.attributes.send_type) === 'flash').length,
        rotpunk: styleSends.filter(s => getSendCategory(s.attributes.send_type) === 'rotpunk').length,
      };
    }).filter(d => d.on_sight + d.flash + d.rotpunk > 0);
  }, [sends, t]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('stats.climbStyleDistribution.title')}</CardTitle>
      </CardHeader>
      {data.length === 0 ? (
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('stats.insufficientData')}
        </CardContent>
      ) : (
        <CardContent className="overflow-x-auto px-2 pb-4">
          <div style={{ minWidth: 320 }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend
                  formatter={(value) => t(`stats.categories.${value}`)}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="on_sight" name="on_sight" stackId="a" fill={SEND_COLORS.on_sight} />
                <Bar dataKey="flash" name="flash" stackId="a" fill={SEND_COLORS.flash} />
                <Bar dataKey="rotpunk" name="rotpunk" stackId="a" fill={SEND_COLORS.rotpunk} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
